import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { MapModalComponent } from 'app/shared/components/map-modal/map-modal.component';
import {
  MapModalTypeEnum,
  MonitorDocumentTypeEnum,
} from 'app/shared/components/map-modal/models/map-modal.enum';
import { MapModalInputData } from 'app/shared/components/map-modal/models/map-modal.interface';
import { CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { BusinessPartnerBySubRoute } from '../../../../interfaces/busines-partner-by-sub-route.interface';
import { hasValidGeolocation } from '../../../utils/has-valid-geolocation.util';

@Component({
  selector: 'app-address-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslatePipe],
  templateUrl: './address-cell-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressCellRendererComponent {
  private dialog = inject(MatDialog);
  private i18n = inject(LanguageService);

  readonly params = input.required<CellRendererParams<BusinessPartnerBySubRoute>>();

  readonly data = computed(() => this.params()?.data);

  readonly principalAddress = computed(() => this.data()?.principalAddress);

  readonly address = computed(() => this.principalAddress()?.address ?? null);

  readonly locationTwo = computed(() => this.principalAddress()?.locationTwo?.name ?? null);

  readonly locationThree = computed(() => this.principalAddress()?.locationThree?.name ?? null);

  readonly locationText = computed(() => {
    const parts = [this.locationTwo(), this.locationThree()].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  });

  readonly hasGeolocation = computed(() =>
    hasValidGeolocation(this.principalAddress()?.geolocationPoint)
  );

  openMapModal(): void {
    const bpData = this.data();
    if (!bpData || !this.hasGeolocation()) return;

    const mapData: MapModalInputData = {
      type: MapModalTypeEnum.SIMPLE,
      tripId: null,
      element: bpData,
      document: MonitorDocumentTypeEnum.NONE,
      groupDocuments: [],
      map: {
        displayMode: 'drawer',
        captions: { title: this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.MAP_DEFAULT_TITLE') },
      },
    };

    this.dialog.open(MapModalComponent, {
      width: '90%',
      height: '80%',
      data: mapData,
    });
  }
}
